import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../../../components/Header/Header";
import IndentDetails from "../IndentPage/ItemsPage/IndentDetails";
import ItemsTable from "./PEItemsTable";
import { useSelector } from "react-redux";
import IndentChangesTracker from "../IndentChangesTracker";
import { Loader2 } from "lucide-react";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl flex items-center space-x-4">
      <Loader2 className="animate-spin" size={24} />
      <span className="text-lg">Processing...</span>
    </div>
  </div>
);


const useIndentData = (indentId, apiEndpoint) => {
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!indentId) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [indentResponse, itemsResponse] = await Promise.all([
          axios.get(apiEndpoint),
          axios.get(`${API_BASE_URL}/viewTable/indentitems/${indentId}`)
        ]);

        const indentData = Array.isArray(indentResponse.data)
          ? indentResponse.data[0]
          : indentResponse.data;

        if (indentData) {
          setPageData({
            ...indentData,
            IndentItems: itemsResponse.data || []
          });
        }
      } catch (err) {
        setError(err.message);
        setPageData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [indentId, apiEndpoint]);

  return { pageData, isLoading, error };
};


const InvoicingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { row: initialRow } = location.state || {};
  const [row, setRow] = useState(initialRow);

  const [items, setItems] = useState([]);
  const [isDraft, setIsDraft] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditable, setIsEditable] = useState(true);
  const [categories, setCategories] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const currentUserId = useSelector((state) => state.auth.id);
  const [changeLog, setChangeLog] = useState({
    addedItems: [],
    deletedItems: [],
    updatedItems: [],
  });
  const [storedChangeLogs, setStoredChangeLogs] = useState([]);

  const apiEndpoint = `${API_BASE_URL}/l2page/indents/${initialRow.IndentID}`;

  const { pageData, isLoading: pageDataLoading, error: pageError } = useIndentData(
    row?.IndentID,
    apiEndpoint
  );

  const refreshPage = async () => {
    setIsLoading(true);
    try {
      await fetchDraftItems();
      await fetchChangeLogs();
      setRefreshKey((prevKey) => prevKey + 1); // Force re-render
    } catch (error) {
      console.error("Error refreshing page:", error);
      setError("Failed to refresh page data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDraftItems = async () => {
    if (row && row.IndentID) {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/viewTable/indentitems`,
          {
            params: { indentId: row.IndentID },
          }
        );

        const indentStatus =
          response.data.length > 0
            ? {
                l1: response.data[0].L1A || 0,
                l2: response.data[0].L2 || 0,
                po: response.data[0].PO || 0,
                ph: response.data[0].PH || 0,
                so: response.data[0].IR || 0,
              }
            : { l1: 0, l2: 0, po: 0, ph: 0, so: 0 };

        const draftItems = response.data.map((item) => ({
          itemID: item.ItemID,
          itemName: item.ItemName,
          quantity: item.Quantity,
          stock: item.Stock,
          categoryID: item.CategoryID,
          remarks: item.Description,
          promisedDate: item.PromisedDate,
          total: item.Quantity * item.Stock,
          accountingUnitID: item.AccountingUnitID,
        }));

        setItems(draftItems);
        setIsDraft(true);

        row.L1 = indentStatus.l1;
        row.L2 = indentStatus.l2;
        row.PO = indentStatus.po;
        row.PH = indentStatus.ph;
        row.IR = indentStatus.so;
      } catch (error) {
        console.error("Error fetching draft items:", error);
        setError("Failed to load draft items");
      }
    }
  };

  const fetchChangeLogs = async () => {
    if (!row?.IndentID) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/change_log/change-logs/${row.IndentID}`
      );
      setStoredChangeLogs(response.data);
    } catch (error) {
      console.error("Error fetching change logs:", error);
    }
  };

  useEffect(() => {
    if (!row?.IndentID) {
      navigate("/peindent");
      return;
    }
    fetchDraftItems();
    fetchChangeLogs();
  }, [row, refreshKey]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoryResponse = await axios.get(
          `${API_BASE_URL}/dropdown/mcatdrop`
        );
        setCategories(categoryResponse.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const saveChangeLog = async (changeLogData) => {
    try {
      await axios.post(`${API_BASE_URL}/change_log/change-logs`, {
        indentId: row.IndentID,
        userId: currentUserId,
        currentlevel: "Indent",
        ...changeLogData,
      });
    } catch (error) {
      console.error("Error saving change log:", error);
      setError("Failed to save change log");
    }
  };

  const handleItemUpdate = async (updateInfo) => {
    setIsLoading(true);
    try {
      if (updateInfo.type === "add") {
        await saveChangeLog({ type: "add", item: updateInfo.item });
        await refreshPage(); // Refresh the entire page
      } else if (updateInfo.type === "update" && updateInfo.items) {
        setItems(updateInfo.items);
        await saveChangeLog({
          type: "update",
          original: items.find(
            (item) => item.itemID === updateInfo.item.itemID
          ),
          updated: updateInfo.item,
        });
        await refreshPage(); // Refresh after update
      } else if (updateInfo.type === "delete") {
        await saveChangeLog({ type: "delete", item: updateInfo.item });
        await refreshPage(); // Refresh after delete
      }
    } catch (error) {
      console.error("Error updating items:", error);
      setError("Failed to update items");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = (newItem) => {
    // Validate item before adding
    if (!newItem.itemID || newItem.quantity <= 0 || newItem.stock < 0) {
      setError("Invalid item details");
      return;
    }

    setItems((prevItems) => [
      ...prevItems,
      {
        ...newItem,
        itemName: newItem.itemName || "Unnamed Item", // Fallback name if not provided
      },
    ]);
    setChangeLog((prev) => ({
      ...prev,
      addedItems: [...prev.addedItems, newItem],
    }));
    setError(null);
  };

  const handleDeleteItem = (index) => {
    const deletedItem = items[index];
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
    setChangeLog((prev) => ({
      ...prev,
      deletedItems: [...prev.deletedItems, deletedItem],
    }));
  };

  const calculateTotalAmount = () => {
    return items.reduce((acc, item) => acc + item.total, 0).toFixed(2);
  };

  const handleSaveDraft = async () => {
    if (!row || !row.IndentID) {
      setError("No indent selected");
      return;
    }

    if (items.length === 0) {
      setError("Please add items before saving draft");
      return;
    }

    try {
      // Prepare draft submission data to match backend expectations
      await axios.put(
        `${API_BASE_URL}/viewTable/indents/${row.IndentID}/draft`,
        {
          isDraft: true,
          items: items.map((item) => ({
            itemID: item.itemID,
            quantity: item.quantity,
            remarks: item.remarks,
            categoryID: item.categoryID,
            stock: item.stock,
            promisedDate: item.promisedDate,
          })),
        }
      );

      // Navigate back to indents page with success message
      navigate("/peindent", {
        state: {
          message: "Draft saved successfully",
        },
      });
    } catch (error) {
      setError("Failed to save draft. Please try again.");
      console.error("Draft saving error:", error);
    }
  };

  const handleSubmit = useCallback(async () => {
    if (items.length === 0) {
      setError("Please add items before submitting");
      return;
    }

    try {
      const submissionData = {
        indentId: row.IndentID,
        userId: row.UserID,
        items: items.map((item) => ({
          itemID: item.itemID,
          quantity: item.quantity,
          stock: item.stock,
          remarks: item.remarks || "",
          categoryID: item.categoryID,
          promisedDate: item.promisedDate,
          accountingUnitID: item.accountingUnitID || null,
        })),
      };

      const response = await axios.post(
        `${API_BASE_URL}/viewTable/indents/submit`,
        submissionData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Explicitly check for specific success conditions
      if (response.data && response.data.message) {
        setIsDraft(false);
        setIsEditable(false);

        navigate("/PEindent", {
          state: {
            message: response.data.message || "Indent submitted successfully",
          },
        });
      }
    } catch (error) {
      // More detailed error handling
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Submission failed. Please try again.";

      setError(errorMessage);

      // Log the full error for debugging
      console.error("Submission Error:", error.response?.data || error);
    }
  }, [row, currentUserId, items, navigate]);

  const handlePrint = () => {
    // Implement your print logic here
    window.print(); // Basic print functionality
    // Or use a more advanced printing library
  };

  return (
    <div>
      <Header
        exportEndpoint={apiEndpoint}
        importEndpoint={apiEndpoint}
        currentIndentData={pageData}
      />{" "}
      <div className="container mx-auto p-6">
        {isLoading && <LoadingOverlay />}
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Indent Request</h3>
        </div>
        {row && (
          <>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            <IndentDetails row={row} userId={currentUserId} />
            <ItemsTable
              key={refreshKey} // Add this to force re-render of ItemsTable
              indentID={row.IndentID}
              items={items}
              onUpdateItem={handleItemUpdate}
              calculateTotalAmount={calculateTotalAmount}
              isDraft={isDraft}
              onSubmit={handleSubmit}
              onSaveDraft={handleSaveDraft}
              onPrint={handlePrint}
              isEditable={isEditable}
              indentStatus={{
                l1: row.L1 || 0,
                l2: row.L2 || 0,
                po: row.PO || 0,
                ph: row.PH || 0,
                so: row.IR || 0,
              }}
            />
            <IndentChangesTracker
              indentID={row.IndentID.toString()}
              changes={changeLog}
              storedChangeLogs={storedChangeLogs}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default InvoicingPage;
